<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { createRealEstateBrokerInvestmentPresenter } from '../composables/realEstateBrokerInvestmentPresenter';

const props = defineProps({
  loading: { type: Boolean, default: false },
  summary: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { money, temperatureLabel } =
  createRealEstateBrokerInvestmentPresenter(t);
const temperatures = computed(() => props.summary.temperaturas || []);
const difference = computed(() => Number(props.summary.diferenca_vgv || 0));
const differenceKey = computed(() => {
  if (difference.value > 0) return 'INCREASE';
  if (difference.value < 0) return 'DECREASE';

  return 'UNCHANGED';
});
const differenceLabel = computed(
  () =>
    ({
      INCREASE: t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.INCREASE'),
      DECREASE: t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.DECREASE'),
      UNCHANGED: t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.UNCHANGED'),
    })[differenceKey.value]
);
const differenceClass = computed(
  () =>
    ({
      INCREASE: 'bg-green-100 text-green-700',
      DECREASE: 'bg-red-100 text-red-700',
      UNCHANGED: 'bg-slate-100 text-slate-700',
    })[differenceKey.value]
);
const differenceIcon = computed(
  () =>
    ({
      INCREASE: 'i-lucide-arrow-up',
      DECREASE: 'i-lucide-arrow-down',
      UNCHANGED: '',
    })[differenceKey.value]
);
const colors = { frio: '#aca9ff', morno: '#74848b', quente: '#ff7878' };
const segments = computed(() => {
  let offset = 0;

  return temperatures.value.map(item => {
    const value = Math.max(0, Math.min(100, Number(item.porcentagem || 0)));
    const segment = {
      ...item,
      color: colors[item.slug] || '#e0e7eb',
      value,
      offset,
    };
    offset += value;
    return segment;
  });
});
</script>

<template>
  <LaisCard density="tight" data-testid="broker-investment-portfolio-chart">
    <div class="p-4 sm:p-5">
      <div
        class="flex items-center justify-between gap-3 border-b border-lais-border-soft pb-3"
      >
        <h2 class="font-redHatDisplay text-lg font-semibold text-lais-text">
          {{ t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.TITLE') }}
        </h2>
      </div>
      <div
        class="grid gap-5 pt-5 lg:grid-cols-[minmax(0,1fr)_234px] lg:items-center"
      >
        <div class="space-y-3">
          <p class="text-2xl font-bold text-lais-text">
            {{
              loading
                ? t('REAL_ESTATE_BROKER_INVESTMENT.TABLE.LOADING')
                : money(summary.vgv)
            }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
              :class="differenceClass"
            >
              <span
                v-if="differenceIcon"
                class="size-3.5"
                :class="differenceIcon"
                aria-hidden="true"
              />
              {{ differenceLabel }}
            </span>
            <span
              v-if="difference"
              class="text-sm font-semibold text-lais-text"
            >
              {{ money({ amount: difference }) }}
            </span>
          </div>
          <span
            class="inline-flex rounded-full bg-lais-bg-soft px-2.5 py-1 text-xs font-semibold text-lais-text-muted"
          >
            {{
              t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.OPTIONS', {
                count: summary.quantidade_total || 0,
              })
            }}
          </span>
        </div>
        <div class="mx-auto w-full max-w-[234px]">
          <svg
            viewBox="0 0 234 118"
            role="img"
            :aria-label="
              t('REAL_ESTATE_BROKER_INVESTMENT.PORTFOLIO.CHART_ARIA')
            "
          >
            <path
              d="M 16 112 A 101 101 0 0 1 218 112"
              fill="none"
              pathLength="100"
              stroke="#e0e7eb"
              stroke-width="24"
            />
            <path
              v-for="item in segments"
              :key="item.slug"
              d="M 16 112 A 101 101 0 0 1 218 112"
              fill="none"
              pathLength="100"
              :stroke="item.color"
              stroke-width="24"
              :stroke-dasharray="`${item.value} ${100 - item.value}`"
              :stroke-dashoffset="-item.offset"
            />
          </svg>
          <div
            class="mt-2 grid grid-cols-3 gap-2 text-center text-xs text-lais-text-muted"
          >
            <span v-for="item in segments" :key="`legend-${item.slug}`">
              <b :style="{ color: item.color }">{{
                t('REAL_ESTATE_BROKER_INVESTMENT.SUMMARY.PERCENTAGE', {
                  value: item.value,
                })
              }}</b>
              {{ temperatureLabel(item.slug) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </LaisCard>
</template>
